import axios from "axios";

const IMPERSONATION_SESSION_KEY = "impersonationSession";
const anonymousAxios = axios.create();

function getImpersonationSession() {
  try {
    const value = sessionStorage.getItem(IMPERSONATION_SESSION_KEY);
    const session = value ? JSON.parse(value) : null;

    if (session && (!session.jwt || !session.userRole)) {
      throw new Error("Invalid impersonation session");
    }

    return session;
  } catch (e) {
    sessionStorage.removeItem(IMPERSONATION_SESSION_KEY);
    return null;
  }
}

const impersonationSession = getImpersonationSession();

export default {
  state: {
    token: impersonationSession?.jwt || localStorage.getItem("token") || null,
    role:
      impersonationSession?.userRole || localStorage.getItem("role") || null,
    mustChangePassword: impersonationSession
      ? false
      : localStorage.getItem("mustChangePassword") === "true",
    userId:
      impersonationSession?.userId || localStorage.getItem("userId") || null,
    isImpersonating: !!impersonationSession,
    impersonatedUser: impersonationSession,
  },
  getters: {
    isLoggedIn: (state) =>
      !!state.token && state.role && state.role != "assignee",
    isImpersonating: (state) => state.isImpersonating,
  },
  mutations: {
    login(state, user) {
      state.token = user.token;
      state.role = user.role;
      state.mustChangePassword = !!user.mustChangePassword;
      state.userId = user.userId || null;

      localStorage.setItem(
        "mustChangePassword",
        state.mustChangePassword ? "true" : "false",
      );
      if (state.userId) localStorage.setItem("userId", state.userId);
      else localStorage.removeItem("userId");
    },
    setImpersonation(state, session) {
      state.token = session.jwt;
      state.role = session.userRole;
      state.mustChangePassword = false;
      state.userId = session.userId || null;
      state.isImpersonating = true;
      state.impersonatedUser = session;

      sessionStorage.setItem(
        IMPERSONATION_SESSION_KEY,
        JSON.stringify(session),
      );
    },
    logout(state, { preserveSharedSession = false } = {}) {
      state.token = null;
      state.role = null;
      state.mustChangePassword = false;
      state.userId = null;
      state.isImpersonating = false;
      state.impersonatedUser = null;

      sessionStorage.removeItem(IMPERSONATION_SESSION_KEY);

      if (!preserveSharedSession) {
        localStorage.removeItem("mustChangePassword");
        localStorage.removeItem("userId");
      }
    },
  },
  actions: {
    async login({ commit, dispatch }, credentials) {
      const res = await axios.post(
        `${process.env.VUE_APP_BASE_URL}/User/login`,
        credentials,
      );

      const token = res.data.jwt;
      const role = res.data.userRole;
      const mustChangePassword = res.data.mustChangePassword;
      const userId = res.data.userId;
      console.log(res.data);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      localStorage.setItem("token", token);
      localStorage.setItem("role", role);

      if (role == "co" || role == "gm") {
        await Promise.all([
          dispatch("getInfo", role),
          dispatch("getQuestionnaires", role),
          dispatch("getGroupsList", { role }),
        ]);
      }

      if (role == "psysupport") {
        await Promise.all([dispatch("getInfo", role)]);
      }

      if (role == "filistos") {
        await Promise.all([
          dispatch("getInfo", role),
          dispatch("getQuestionnaires", role),
          dispatch("getClientsList", role),
        ]);
      }

      if (role == "allpsysup") {
        await Promise.all([
          dispatch("getInfo", role),
          dispatch("getClientsList", role),
        ]);
      }

      commit("login", {
        token,
        role,
        mustChangePassword,
        userId,
      });

      return { role, mustChangePassword: !!mustChangePassword, userId };
    },
    async issueImpersonationCode(_, targetUserId) {
      const res = await axios.post(
        `${process.env.VUE_APP_BASE_URL}/superadmin/ImpersonateUser/${targetUserId}`,
      );

      return res.data;
    },
    async exchangeImpersonationCode({ commit, dispatch }, exchangeCode) {
      const res = await anonymousAxios.post(
        `${process.env.VUE_APP_BASE_URL}/superadmin/ExchangeImpersonationCode/${exchangeCode}`,
      );

      await dispatch("logout", {
        preserveSharedSession: true,
        withoutRefresh: true,
      });
      commit("setImpersonation", res.data);

      return res.data;
    },
    async changeOwnPassword(_, payload) {
      await axios.post(
        `${process.env.VUE_APP_BASE_URL}/User/ChangeOwnPassword`,
        payload,
      );
    },
    async sendForgotPasswordEmail({ commit }, email) {
      try {
        await axios.post(
          `${process.env.VUE_APP_BASE_URL}/User/AskPasswordReset`,
          {
            userEmail: email,
          },
        );

        commit("toggleSnackbar", {
          open: true,
          color: "success",
          text:
            "Email has been sent! Follow the instructions to change your password.",
          timeout: 5000,
        });
      } catch (e) {
        commit("toggleSnackbar", {
          open: true,
          color: "error",
          text:
            "Something went wrong on application initialization. Please try again later",
          timeout: -1,
        });

        throw e;
      }
    },
    async resetPassword({ commit }, payload) {
      try {
        await axios.post(`${process.env.VUE_APP_BASE_URL}/User/SetPassword`, {
          passwordResetToken: payload.token,
          newPassword: payload.password,
        });

        commit("toggleSnackbar", {
          open: true,
          color: "success",
          text: "Your password has been changed! You can now login.",
          timeout: 5000,
        });
      } catch (e) {
        commit("toggleSnackbar", {
          open: true,
          color: "error",
          text:
            "Something went wrong on application initialization. Please try again later",
          timeout: -1,
        });

        throw e;
      }
    },
    async logout({ commit, state }, payload) {
      const preserveSharedSession =
        state.isImpersonating ||
        payload?.fromAssignee ||
        payload?.preserveSharedSession;

      commit("logout", { preserveSharedSession });
      commit("clearBaseState");

      commit("clearDashboardState");
      commit("clearUsersState");
      commit("clearGroupsState");
      commit("clearAssignmentsListState");

      commit("clearSuperAdminDashboardState");
      commit("clearClientsState");

      delete axios.defaults.headers.common["Authorization"];

      if (!preserveSharedSession) {
        localStorage.removeItem("token");
        localStorage.removeItem("role");
      }

      if (!payload?.withoutRefresh) {
        location.reload();
      }
    },
  },
};
